/**
 * ChequeVoucher — Printable record for a Cheque entry.
 *
 * Rendered inside MasterReportPreview (which handles the print modal,
 * @media print isolation, and the Print button).
 */

import type { ReactNode } from 'react';
import type { ChequeRecordDto } from '@/types/cheque.types';
import { CHEQUE_TYPE_LABELS, CHEQUE_STATUS_LABELS, toChequeStatus } from '@/types/cheque.types';
import { VoucherLetterhead } from '@/components/master/report/VoucherLetterhead';
import { CROSSING_LABELS } from '@/constants/chequeCrossing';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.6 }}>
    <span style={{ color: '#6b7280', minWidth: '140px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }: { status: string | number }) => {
  // Normalised: the API serialises ChequeStatus by value, so the raw field is 1, not "Issued" —
  // which printed a bare "1" in the chip where the status should be.
  const key = toChequeStatus(status as never);
  const colour: Record<string, { bg: string; text: string }> = {
    Draft:     { bg: '#f3f4f6', text: '#374151' },
    Received:  { bg: '#dbeafe', text: '#1e40af' },
    Deposited: { bg: '#e0e7ff', text: '#3730a3' },
    Realized:  { bg: '#d1fae5', text: '#065f46' },
    Returned:  { bg: '#ffedd5', text: '#9a3412' },
    Cancelled: { bg: '#f3f4f6', text: '#6b7280' },
    Issued:    { bg: '#dbeafe', text: '#1e40af' },
    Presented: { bg: '#fef9c3', text: '#854d0e' },
    Cleared:   { bg: '#a7f3d0', text: '#064e3b' },
    Stopped:   { bg: '#fee2e2', text: '#991b1b' },
  };
  const c = colour[key] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
    }}>
      {CHEQUE_STATUS_LABELS[key] ?? key}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface ChequeVoucherProps {
  cheque:     ChequeRecordDto;
  printedBy?: string;
}

export const ChequeVoucher = ({
  cheque,
  printedBy,
}: ChequeVoucherProps): ReactNode => {

  const partyLabel = cheque.partyType === 'Customer' ? 'Customer'
    : cheque.partyType === 'Supplier' ? 'Supplier'
    : 'Party';

  const partyCode = cheque.partyType === 'Customer' ? cheque.customerCode
    : cheque.partyType === 'Supplier' ? cheque.supplierCode
    : null;

  const partyName = cheque.partyType === 'Customer' ? cheque.customerName
    : cheque.partyType === 'Supplier' ? cheque.supplierName
    : null;

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>

      {/* ── Voucher Header ──────────────────────────────────────────────────── */}
      <VoucherLetterhead title="Cheque Record">
        <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Cheque No.
        </div>
        <div style={{ fontSize: '17px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
          {cheque.chequeNumber}
        </div>
        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
          Cheque date
        </div>
        <div style={{ fontSize: '11px', color: '#111827', fontWeight: 500 }}>
          {fmtDate(cheque.chequeDate)}
        </div>
        <div style={{ marginTop: '6px' }}>
          <StatusBadge status={cheque.chequeStatus} />
        </div>
        <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px' }}>
          Printed: {new Date().toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })}
          {printedBy && ` · ${printedBy}`}
        </div>
      </VoucherLetterhead>

      {/* ── Info grid ───────────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 32px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Bank Account
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{cheque.bankAccountName}</div>
          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace', marginBottom: '4px' }}>{cheque.bankAccountCode}</div>
          <LabelValue label="Cheque Type" value={CHEQUE_TYPE_LABELS[cheque.chequeType] ?? cheque.chequeType} />
          <LabelValue label="Cheque Date" value={fmtDate(cheque.chequeDate)} />
          <LabelValue label="Currency"    value={cheque.currencyCode} />
        </div>
        <div>
          {/* Who the instrument is payable to, as written on it. Falls back to the party name,
              which is what the cheque prints when no payee was stated (GAP-004). */}
          <LabelValue label="Payee" value={cheque.payeeName || partyName || '—'} />
          {cheque.crossingType && (
            <LabelValue label="Crossing" value={CROSSING_LABELS[cheque.crossingType] ?? cheque.crossingType} />
          )}
          {partyCode && <LabelValue label={`${partyLabel} Code`} value={partyCode} />}
          {partyName && <LabelValue label={`${partyLabel} Name`} value={partyName} />}
          {cheque.referenceDocument && (
            <LabelValue label="Related Document" value={cheque.referenceDocument} />
          )}
          {cheque.statusChangedDate && (
            <LabelValue label="Status Changed" value={fmtDate(cheque.statusChangedDate)} />
          )}
          {cheque.statusChangeReason && (
            <LabelValue label="Status Reason" value={cheque.statusChangeReason} />
          )}
        </div>
      </div>

      {/* Remarks */}
      {cheque.remarks && (
        <div style={{
          marginBottom: '16px',
          padding: '8px 12px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#374151',
        }}>
          <span style={{ color: '#6b7280', fontWeight: 600 }}>Remarks: </span>
          {cheque.remarks}
        </div>
      )}

      {/* ── Amount section ───────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <div style={{ width: '260px' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '5px 8px',
            background: '#1e3a5f',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: 'white',
          }}>
            <span>Amount ({cheque.currencyCode}):</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtNum(cheque.amount)}</span>
          </div>
        </div>
      </div>

      {/* ── Status history ───────────────────────────────────────────────────── */}
      {cheque.statusChangedBy && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Status History
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Changed By</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Date</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '4px 8px', color: '#111827', fontWeight: 500 }}>{cheque.statusChangedBy}</td>
                <td style={{ padding: '4px 8px', color: '#374151', fontFamily: 'monospace' }}>{fmtDate(cheque.statusChangedDate)}</td>
                <td style={{ padding: '4px 8px', color: '#374151' }}>{cheque.statusChangeReason ?? '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginTop: '32px',
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
      }}>
        {['Prepared By', 'Verified By', 'Authorised By'].map((label) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              paddingTop: '4px',
              fontSize: '9px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
